// "I submitted" — a person records that they sent a queued claim themselves.
//
// Never optimistic. The server refuses while a skill's lease is live, when the row
// is already finished, when the reference is already recorded for this claim, and
// for Claim.MD through the portal when the reference is not Claim.MD's claim id.
// The row only changes when the 200 comes back; a refusal keeps the modal open
// with the server's sentence.
//
// Focus goes to the reference, never to the confirm button, and Escape closes.

import React, { useEffect, useRef, useState } from "react";
import { markSubmitted } from "./api";
import useReturnFocus from "./useReturnFocus";
import "./Queue.css";

// A's channels (docs/submit-queue.md), in the words a biller uses.
const CHANNELS = [
  { key: "portal_ui", label: "Portal website — I entered it myself" },
  { key: "manual", label: "Phone, fax or mail" },
  { key: "other", label: "Other" },
];

export default function SubmittedModal({
  practiceId,
  item,
  portals = [],
  onClose,
  onStale,
  onSubmitted,
}) {
  const [ref, setRef] = useState("");
  const [channel, setChannel] = useState("");
  // One lineage: that portal, sent explicitly. Several: the biller picks. None:
  // omitted, and the server records the far side as unknown.
  const [portal, setPortal] = useState(
    portals && portals.length === 1 ? portals[0] : "",
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const refInput = useRef(null);

  useReturnFocus(refInput);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  // `portals` is null when the practice's portals could not be loaded.
  const portalsUnknown = portals === null;
  const portalList = portals || [];
  // The portal only matters for a portal submission; for phone/fax/other it is not
  // sent. Ruled server-side: from A's phase 2 every non-portal channel is recorded
  // as `manual` whatever the body says, so the UI sends the channel the biller
  // picked and does not work around the resolution.
  const viaPortal = channel === "portal_ui";
  const needsPortal = viaPortal && portalList.length > 1;
  const valid =
    ref.trim().length > 0 &&
    channel !== "" &&
    !(viaPortal && portalsUnknown) &&
    (!needsPortal || portal !== "");
  const claimmdPortalUi = viaPortal && portal === "claimmd";

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    try {
      const body = await markSubmitted(practiceId, item.id, {
        confirmationRef: ref.trim(),
        channel,
        portal: viaPortal ? portal || undefined : undefined,
      });
      onSubmitted(item, body);
    } catch (err) {
      setError(err.message);
      setBusy(false);
      // 409: the row changed under this page (someone else acted on it). Refresh
      // the list behind the modal so the row stops offering stale actions.
      if (err.status === 409 && onStale) onStale();
      // The fields were disabled while busy, which drops focus to <body>; give it
      // back so a keyboard user can correct and retry.
      setTimeout(() => refInput.current?.focus(), 0);
    }
  };

  return (
    <div className="sq-modal-backdrop" onClick={() => !busy && onClose()}>
      <div
        className="sq-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sq-submitted-title"
      >
        <div className="sq-modal-header">
          <h2 id="sq-submitted-title">I submitted this claim</h2>
          <button
            className="sq-modal-close"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="sq-modal-body">
          <div className="sq-modal-summary">
            <span>
              Claim <strong>#{item.claimId}</strong>
            </span>
            <span>DOS {item.dateOfService || "—"}</span>
            <span>{item.payerName || "—"}</span>
          </div>

          {item.status === "held" && (
            <div className="sq-message warn">
              <span>
                Submit was pressed on this claim and no reference was captured.
                Record it only if you found the claim in the portal, with the
                reference the portal shows.
              </span>
            </div>
          )}

          <p className="sq-modal-lede">
            This records the claim as sent and takes it off the queue, so nobody
            — person or skill — sends it again. It cannot be undone from this
            page.
          </p>

          <label className="sq-field">
            <span>Confirmation reference</span>
            <input
              ref={refInput}
              type="text"
              maxLength={100}
              value={ref}
              disabled={busy}
              onChange={(e) => setRef(e.target.value)}
            />
            <small className="sq-field-hint">
              {claimmdPortalUi
                ? "Claim.MD’s own claim id (digits), as Claim.MD shows it. Atlas matches Claim.MD’s responses on it."
                : "The portal’s claim/ICN number, or describe what you have if there is none."}
            </small>
          </label>

          <label className="sq-field">
            <span>How was it sent?</span>
            <select
              value={channel}
              disabled={busy}
              onChange={(e) => setChannel(e.target.value)}
            >
              <option value="">Choose…</option>
              {CHANNELS.map((c) => (
                <option key={c.key} value={c.key}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>

          {viaPortal && portalsUnknown && (
            <div className="sq-message error" role="alert">
              Atlas could not load this practice’s portals, so a portal
              submission cannot be recorded against the right one. Close this,
              refresh the page and try again.
            </div>
          )}

          {needsPortal && (
            <label className="sq-field">
              <span>Which portal?</span>
              <select
                value={portal}
                disabled={busy}
                onChange={(e) => setPortal(e.target.value)}
              >
                <option value="">Choose…</option>
                {portalList.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </label>
          )}

          {error && (
            <div className="sq-message error" role="alert">
              {error}
            </div>
          )}
        </div>

        <div className="sq-modal-footer">
          <button
            className="btn btn-secondary btn-sm"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={submit}
            disabled={!valid || busy}
          >
            {busy ? "Recording…" : `Record claim #${item.claimId} as sent`}
          </button>
        </div>
      </div>
    </div>
  );
}
